/**
 * How the game's blocking backend bridge (FWP.net_run / net_fail) reacts to a failed call (port addition; the
 * original retried every failure after main_timeout). Only protocol error codes are inspected here: the game never
 * imports transports or protocol modules (tests/integration/no-vk.test.ts).
 */
import type { NetRetryReason } from './net-messages';

/**
 * The transport layer's own failure time of a call (src/client/net: BackendClient makes 2 attempts, 1 retry after
 * 250 ms, and each waits the full per-request timeout, 15 s by default). The game may not import the client modules;
 * tests/integration/net-watchdog.test.ts checks that these numbers agree with client.ts requestBudgetMs.
 */
export const NET_REQUEST_TIMEOUT_MS = 15_000;
export const NET_REQUEST_ATTEMPTS = 2;
export const NET_REQUEST_RETRY_PAUSE_MS = 250;
/** Extra wait of the bridge watchdog over the transport's own failure time. */
export const NET_WATCHDOG_SLACK_MS = 5_000;

/**
 * Bridge-clock time after which the watchdog fails an unsettled blocking call and retries it: longer than the whole
 * time the transport takes to fail it (so a slow but live server is not reported as lost, and a non-mutating call is
 * not sent twice in parallel), rounded up to whole seconds. `requestTimeoutMs`: the call's per-request timeout (the
 * one passed to GameServices, e.g. a long battle validation), default the transport's 15 s.
 */
export function netCallTimeoutMs(requestTimeoutMs: number = NET_REQUEST_TIMEOUT_MS): number {
  const transportMs = NET_REQUEST_ATTEMPTS * requestTimeoutMs + (NET_REQUEST_ATTEMPTS - 1) * NET_REQUEST_RETRY_PAUSE_MS;
  return Math.ceil((transportMs + NET_WATCHDOG_SLACK_MS) / 1000) * 1000;
}

/** Watchdog limit of a blocking call with the default per-request timeout (36 s). */
export const NET_CALL_TIMEOUT_MS = netCallTimeoutMs();
/** INTERNAL (a server-side failure) is retried this many times before it is reported. */
export const NET_INTERNAL_RETRIES = 2;
/** Re-authentications per call after UNAUTHORIZED before the session is reported as expired. */
export const NET_REAUTH_ATTEMPTS = 2;

export interface NetBan {
  code: number;
  reason: string;
  /** Epoch ms, or null for a permanent ban. */
  until: number | null;
}

export type NetFailure =
  | { action: 'retry'; code: string; reason: NetRetryReason }
  | { action: 'reauth'; code: string }
  | { action: 'banned'; code: string; ban: NetBan }
  | { action: 'conflict'; code: string }
  | { action: 'version'; code: string }
  | { action: 'fatal'; code: string };

/** Protocol error code of a failure; errors without one (a bug in a call) count as INTERNAL. */
export function netErrorCode(err: any): string {
  return err && typeof err.code === 'string' && /^[A-Z_]+$/.test(err.code) ? err.code : 'INTERNAL';
}

/**
 * A failure outside the blocking bridge (the online lobby and battles): when it is UNAUTHORIZED - the backend services
 * already renewed the session if they could, so it is over (expired, ended from another device) - and the game plays
 * with accounts, the game restarts into the sign-in screen (FWP.net_session_ended, the path of every other request).
 * True when the failure was handled that way. A guest host (headless tools, tests) keeps its own handling.
 */
export function sessionEnded(fwp: any, err: any): boolean {
  if (netErrorCode(err) !== 'UNAUTHORIZED' || !fwp || typeof fwp.net_accounts !== 'function' || !fwp.net_accounts()) return false;
  fwp.net_session_ended();
  return true;
}

/**
 * Decides what to do with a failed call. `internalFailures` counts the INTERNAL failures of this call so far
 * (including this one); `reauths` the re-authentications already made for it.
 */
export function classifyNetFailure(err: any, internalFailures: number, reauths: number): NetFailure {
  const code = netErrorCode(err);
  switch (code) {
    case 'UNAVAILABLE':
      return { action: 'retry', code, reason: 'offline' };
    case 'RATE_LIMITED':
      return { action: 'retry', code, reason: 'busy' };
    case 'INTERNAL':
      return internalFailures <= NET_INTERNAL_RETRIES ? { action: 'retry', code, reason: 'server' } : { action: 'fatal', code };
    case 'UNAUTHORIZED':
      return reauths < NET_REAUTH_ATTEMPTS ? { action: 'reauth', code } : { action: 'fatal', code };
    case 'BANNED': {
      const b = err && err.details && err.details.ban;
      const ban: NetBan = {
        code: b && Number.isFinite(Number(b.code)) ? Number(b.code) : 0,
        reason: b && b.reason ? String(b.reason) : String((err && err.message) || ''),
        until: b && b.until != null && Number.isFinite(Number(b.until)) ? Number(b.until) : null,
      };
      return { action: 'banned', code, ban };
    }
    case 'CONFLICT':
      return { action: 'conflict', code };
    case 'VERSION_MISMATCH':
      return { action: 'version', code };
    default:
      return { action: 'fatal', code };
  }
}

/** Delay before retry number `attempt` (1-based): 1 s, 2 s, 4 s, ... up to 30 s. */
export function netRetryDelayMs(attempt: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.max(0, attempt - 1));
}

/** Whole hours left of a ban at `nowMs` (the original splash rounds, check_ban FWP.as), or null when permanent. */
export function banHoursLeft(ban: NetBan, nowMs: number): number | null {
  if (ban.until === null) return null;
  return Math.max(0, Math.round((ban.until - nowMs) / 3_600_000));
}
